/**
 * Reports data hooks (S11.1) — read-only, co-located with the reports surface.
 *
 * Reports are pure read lenses over the SO / PO / finance / OCC stores; they
 * perform ZERO writes. Each hook reads a static `/api/`-prefixed cacheKey
 * tracked through `cacheKeyRegistry`, so the owning modules' mutation
 * invalidations (`clearCacheWhere` over `/api/sales-orders`, `/api/occ`, …)
 * reach these caches and a report never shows stale figures after an upstream
 * change. Filtering/derivation happens client-side over the mock-scale data,
 * keeping the keys static.
 *
 * The Back-Order report reuses the shared `useOccLines` hook (same projected
 * lens rows the OCC board reads) so its 待出量 figure is the SAME number by
 * construction — see `backOrderSelectors.ts`.
 */

import { useRequest } from 'ahooks';
import { financeApi, purchaseOrderApi, salesOrderApi } from '@/services/api';
import { trackCacheKey } from '@/utils/cacheKeyRegistry';
import type { SalesOrder } from '@/types/api/salesOrder';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import type { ApInvoice, ArInvoice } from '@/types/api/finance';

// Report tables read the whole (mock-scale) store and derive client-side.
const REPORT_PAGE_SIZE = 500;

const SALES_ORDERS_CACHE_KEY = '/api/sales-orders?view=reports';
const PURCHASE_ORDERS_CACHE_KEY = '/api/purchase-orders?view=reports';
const AR_CACHE_KEY = '/api/finance/ar-invoices?view=reports';
const AP_CACHE_KEY = '/api/finance/ap-invoices?view=reports';

export interface ReportQuery<T> {
  rows: T[];
  isLoading: boolean;
  error?: Error;
  refresh: () => void;
}

/** All sales orders (訂單毛利 + Back-Order price-lock join). */
export function useReportSalesOrders(): ReportQuery<SalesOrder> {
  const { data, loading, error, refresh } = useRequest(
    () => salesOrderApi.list({ pageSize: REPORT_PAGE_SIZE }),
    { cacheKey: trackCacheKey(SALES_ORDERS_CACHE_KEY) }
  );
  return { rows: data?.data ?? [], isLoading: loading, error, refresh };
}

/** All purchase orders (未進貨-供應商). */
export function useReportPurchaseOrders(): ReportQuery<PurchaseOrder> {
  const { data, loading, error, refresh } = useRequest(
    () => purchaseOrderApi.list({ pageSize: REPORT_PAGE_SIZE }),
    { cacheKey: trackCacheKey(PURCHASE_ORDERS_CACHE_KEY) }
  );
  return { rows: data?.data ?? [], isLoading: loading, error, refresh };
}

/** Customer (AR) invoices (未收款). */
export function useReportArInvoices(): ReportQuery<ArInvoice> {
  const { data, loading, error, refresh } = useRequest(() => financeApi.listAr(), {
    cacheKey: trackCacheKey(AR_CACHE_KEY),
  });
  return { rows: data?.data ?? [], isLoading: loading, error, refresh };
}

/** Vendor (AP) invoices (未付款). */
export function useReportApInvoices(): ReportQuery<ApInvoice> {
  const { data, loading, error, refresh } = useRequest(() => financeApi.listAp(), {
    cacheKey: trackCacheKey(AP_CACHE_KEY),
  });
  return { rows: data?.data ?? [], isLoading: loading, error, refresh };
}
